import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { KeyRound, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { portalPath } from '../../lib/portal';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GovResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState({ new_password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = 'Reset password · USKE Procurement Portal'; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) return toast({ title: 'Password too short', variant: 'destructive' });
    if (pw.new_password !== pw.confirm) return toast({ title: "Passwords don't match", variant: 'destructive' });
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw.new_password });
      setDone(true);
      setTimeout(() => navigate(portalPath('/login')), 1500);
    } catch (err) {
      toast({ title: 'Reset failed', description: err.response?.data?.detail || 'Invalid or expired link', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md border-2 border-red-200" data-testid="gov-reset-no-token">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-800">Missing reset token</h1>
            <p className="text-sm text-slate-500 mt-2">This page must be opened from the link in your reset email.</p>
            <Link to={portalPath('/forgot-password')} className="block mt-4">
              <Button>Request a new reset link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 flex items-center justify-center px-4 py-10" data-testid="gov-reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 text-white">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-lg bg-white text-blue-900 mb-3"><KeyRound className="h-7 w-7" /></div>
          <h1 className="text-2xl font-bold">Choose a new password</h1>
        </div>
        <Card className="border-blue-100">
          <CardContent className="p-6 sm:p-8">
            {done ? (
              <div className="text-center space-y-3" data-testid="gov-reset-success">
                <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
                <h2 className="text-lg font-semibold text-slate-800">Password updated</h2>
                <p className="text-sm text-slate-500">Redirecting you to sign in…</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label htmlFor="np">New password <span className="text-xs text-slate-400">(min 8)</span></Label>
                  <Input id="np" type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="gov-reset-new-password" />
                </div>
                <div>
                  <Label htmlFor="cp">Confirm new password</Label>
                  <Input id="cp" type="password" required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="gov-reset-confirm-password" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-blue-700 hover:bg-blue-800 text-white" data-testid="gov-reset-submit">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating…</> : 'Update password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GovResetPassword;
